import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { moduleAccessCode, moduleDeleteCode, moduleReadCode, moduleWriteCode } from '../constants/PermissionCodes';
import { useAuth } from '../contexts/AuthContext';
import { fetchUserAccess } from '../services/accessControlApi';
import { UserAccessContext } from '../types/UserAccessContext';

type PermissionsContextValue = {
  isLoading: boolean;
  access: UserAccessContext | null;
  refreshAccess: () => Promise<void>;
  hasPermission: (code: string) => boolean;
  canAccessModule: (moduleCode: string) => boolean;
  canReadModule: (moduleCode: string) => boolean;
  canWriteModule: (moduleCode: string) => boolean;
  canDeleteModule: (moduleCode: string) => boolean;
  isOrganizationAdmin: boolean;
};

const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [access, setAccess] = useState<UserAccessContext | null>(null);

  const refreshAccess = useCallback(async () => {
    if (isAuthLoading) {
      setIsLoading(true);
      return;
    }

    if (!isAuthenticated) {
      setAccess(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchUserAccess();
      setAccess(data);
    } catch {
      setAccess(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    void refreshAccess();
  }, [refreshAccess, isAuthenticated]);

  const permissionSet = useMemo(
    () => new Set((access?.permissionCodes ?? []).map((code) => code.toLowerCase())),
    [access],
  );

  const hasPermission = useCallback(
    (code: string) => permissionSet.has(code.toLowerCase()),
    [permissionSet],
  );

  const canAccessModule = useCallback(
    (moduleCode: string) => hasPermission(moduleAccessCode(moduleCode)),
    [hasPermission],
  );

  const canReadModule = useCallback(
    (moduleCode: string) => hasPermission(moduleReadCode(moduleCode)),
    [hasPermission],
  );

  const canWriteModule = useCallback(
    (moduleCode: string) => hasPermission(moduleWriteCode(moduleCode)),
    [hasPermission],
  );

  const canDeleteModule = useCallback(
    (moduleCode: string) =>
      hasPermission(moduleDeleteCode(moduleCode)) || hasPermission(moduleWriteCode(moduleCode)),
    [hasPermission],
  );

  const isOrganizationAdmin = useMemo(
    () => (access?.roleCodes ?? []).some((code) => code.toLowerCase() === 'admin'),
    [access?.roleCodes],
  );

  const value = useMemo(
    () => ({
      isLoading,
      access,
      refreshAccess,
      hasPermission,
      canAccessModule,
      canReadModule,
      canWriteModule,
      canDeleteModule,
      isOrganizationAdmin,
    }),
    [
      access,
      canAccessModule,
      canDeleteModule,
      canReadModule,
      canWriteModule,
      hasPermission,
      isLoading,
      isOrganizationAdmin,
      refreshAccess,
    ],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions(): PermissionsContextValue {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions deve ser usado dentro de PermissionsProvider.');
  }
  return context;
}
